/* eslint-disable no-inner-declarations */
import React, { useState, useEffect } from "react";
import TableCell from "@mui/material/TableCell";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Box, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";

//Form fields
import TextInput from "../FormTypes/TextInput/TextInput";
import ColorPickerField from "../FormTypes/ColorPickerField/ColorPickerField";
import RadioInput from "../FormTypes/RadioInput/RadioInput";
import SelectInput from "../FormTypes/SelectInput/SelectInput";
import ButtonsField from "../FormTypes/ButtonsField/ButtonsField";
import SwitchInput from "../FormTypes/SwitchInput/SwitchInput";
import DateTimeInput from "../FormTypes/DateTimeInput/DateTimeInput";
import LookupInput from "../FormTypes/LookupInput/LookupInput";
import CheckboxInput from "../FormTypes/CheckboxInput/CheckboxInput";
import FileUploadField from "../FormTypes/FileUploadField/FileUploadField";
import ImageUploadField from "../FormTypes/ImageUploadField/ImageUploadField";
import ObjectInput2 from "../FormTypes/ObjectInput/ObjectInput2";
import RichTextInput from "../FormTypes/RichTextInput/RichTextInput";
import BasicPhoneNumberField from "../FormTypes/PhoneNumberField/BasicPhoneNumberField";

import moment from "moment";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { ContentState, EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";

import { uploadingTheFile } from "../FormHelpers/UploadingFile";
import { getAuthData } from "../../../../services/storage/LocalStorage";
import { getFileName } from "../../../../services/FilesService";

import FMRegularFormComponent from "./RegularForm";
import FMSectionsFormComponent from "./SectionForm";
import FMMap from "../../FMMap";
import { handlePromptOnClose } from "../../../../state/actions/tabStack";
import store from "../../../../state/configureStore";
import { TextField } from "../FormTypes/TextField";


const StyledTableCell = styled(TableCell)(({ theme }) => ({
	verticalAlign: "top",
	padding: "6px 32px",
	color: theme.palette.common.white,
	fontSize: 14,
	width: "50%",
}));

const CustomizedGrid = styled(Grid)(({ theme }) => ({
	marginBottom: theme.spacing(2),
	marginTop: theme.spacing(2),
}));

const FMFormComponent = ({
	conversation,
	fields,
	collectionData,
	dashboardsData,
	options,
	handleAction,
	updateRedux,
	handleConfirm,
	handleCancel,
	genericError,
	parentTabId,
	parentDocId,
	parentMessageType,
	cardsSetData,
	onCardAction,
	message
}) => {

	const theme = useTheme();
	const largeScreen = useMediaQuery(theme.breakpoints.up("sm"));
	const addManuallyRow = useSelector((state) => state?.filters?.addManuallyRow);

	const [isFileUploading, setIsFileUploading] = useState([]);
	const [disableSubmit, setDisableSubmit] = useState(true);
	const [activeLookupId, setActiveLookupId] = useState(null);

	const [editorState, setEditorState] = useState(EditorState.createEmpty());
	const userDomain = store.getState().domain?.userDomain || null
	const { user: { userTimezone }, } = getAuthData();
	const dispatch = useDispatch();

	const defaultUserTimezone = "Etc/UTC";
	let isFieldEdited = React.useMemo(() => ({ state: false }), []);

	const isEmptyValues = (value) => {
		return (
			value === undefined ||
			value === null ||
			(typeof value === "object" && Object.keys(value).length === 0) ||
			(typeof value === "string" && value.length === 0)
		);
	};

	useEffect(() => {
		handleCheckSubmitButton();
	}, [fields, genericError]);


	//check from child section 
	const handleCheckSubmitButton = (status) => {

		if (!Array.isArray(fields) || fields.length === 0) return;
		
		if(status){
			setDisableSubmit(true)
			return;
		}

		const mandatoryFields = fields.filter((el) =>
			(!el.mandatory && el.validationResult === false) ||
			(el.mandatory && (isEmptyValues(el.value) || el.validationResult === false))
		);

		const editingFields = fields.filter((el) => el.isFieldEditing === true);
		const hasGenericError = genericError && !genericError.validationResult;
		setDisableSubmit(mandatoryFields.length > 0 || editingFields.length > 0 || hasGenericError || options.readOnly);
	};

	useEffect(() => {
		if (!Array.isArray(fields)) return;

		const richTextField = fields.find(
			(el) => el.id === "text" && el.type === "rich_text_area" && el.value
		);

		if (richTextField) {
			// HTML to Draft - onload for single instance only
			const contentBlock = htmlToDraft(richTextField.value);
			if (contentBlock) {
				const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
				const convertedData = EditorState.createWithContent(contentState);
				setEditorState(convertedData);
			}
		};
	}, []);


	const handleChange = (e, type, val) => {
		fields.forEach((el) => {
			if (el?.id !== e?.target?.name) return;

			isFieldEdited.state = true;

			// Normalize value and editing flag based on type
			switch (type) {
				case "lookup":
				case "dropdown":
					el.isFieldEditing = false;
					el.value = val;
					break;
				case "multi_selection":
				case "object_multi_selection":
				case "checkRadioButton":
					el.isFieldEditing = false;
					el.value = e.target.value;
					break;
				case "uploadFile":
					el.value = val;
					break;
				case "removeUploadFile":
					delete el.fileName;
					delete el.value;
					break;
				case "uncheckRadioButton":
					el.isFieldEditing = false;
					el.value = "";
					break;
				case "password":
					el.isFieldEditing = true;
					el.value = e.target.value;
					break;
				default:
					el.isFieldEditing = true;
					el.value = e.target.value;
					break;
			}

			// Reset validation
			delete el.validationResult;
			delete el.info;
			delete el.validationMessage;
			// Sync state
			updateRedux(el);

			// Below changes are done for DEV-4803 - Save Button Not Enabled When Using Prepopulated Data in MS Edge
			// Autofill multiple fields in edge browser
			const userAgent = navigator.userAgent;
			const isEdge = userAgent.includes("Edg");

			if (isEdge) {
				el.isFieldEditing = false;
				handleAction(
					"move",
					el.id,
					el.value === 0 ? 0 : el.value || (type === "color_picker" ? "#0095F2" : "")
				);
			}
		});
	};


	const onEditorContentChange = (data, maxLength) => {
		let rawData = convertToRaw(data.getCurrentContent());
		let charLength = rawData.blocks.reduce((acc, curr) => acc + curr.text.length, 0);

		if (charLength == 0) {
			setEditorState(data);
			updateField("");
			return disableFormSubmit(true);
		} else if (maxLength !== -1 && charLength > maxLength) {
			return toast.warn(`Content length cannot be > ${maxLength}`, {
				position: toast.POSITION.TOP_LEFT,
			});
		}
		setEditorState(data);
		updateField(draftToHtml(rawData));
	};


	const updateField = (value) => {
		fields.forEach((field) => {
			if (field.id === "text" && field.type === "rich_text_area") {
				field.value = value;
				clearValidation(field);
				updateRedux(field);
			}
		});
	};

	const onBlurField = (e, action = "move", type) => {
		const fieldId = e.target.name;

		fields.forEach((field) => {
			if (field.id === fieldId) {
				field.isFieldEditing = false;
				updateRedux(field, true); //true is for add local key as if user searched anything in lookup

				const valueToSend = field.value === 0
					? 0
					: field.value ? field.value : e.target.value || (type === "color_picker" ? "#0095F2" : "");
				handleAction(action, field.id, valueToSend);
			}
		});

		// When the field values are not edited, we just don't show the dialog prompt 
		if (!isFieldEdited.state) {
			updatePromptOnCloseModal(conversation, false);
		}
	};


	const onBlurRichEditorField = (e, action = "move", type) => {
		fields.forEach((field) => {
			if (field.type === "rich_text_area") {
				field.isFieldEditing = false;
				updateRedux(field);

				const valueToSend =
					field.value === 0
						? 0
						: field.value || (type === "color_picker" ? "#0095F2" : "");

				handleAction(action, field.id, valueToSend);
			}
		});

		if (!isFieldEdited.state) {
			updatePromptOnCloseModal(conversation, false);
		}
	};


	const handleChangeSwitch = (e) => {
		const { name, checked } = e.target;
		fields.forEach((field) => {
			if (field.id === name) {
				field.value = checked;
				clearValidation(field);
				updateRedux(field);
				handleAction("move", field.id, field.value);
			}
		});
	};

	const handleChangeCheck = (e) => {
		const { id, name } = e.target;

		fields.forEach((field) => {
			if (field.id === id) {
				if (!field.value || (typeof field.value === "string" && !field.value.trim())) {
					field.value = [];
				}

				if (field.value.includes(name)) {
					field.value = field.value.filter((item) => item !== name);
				} else {
					field.value.push(name);
				}

				clearValidation(field);
				updateRedux(field);
				handleAction("move", field.id, field.value);
			}
		});
	};

	const handleChangeBooleanCheck = (e) => {
		const { id, checked } = e.target;

		fields.forEach((field) => {
			if (field.id === id) {
				field.value = checked
				clearValidation(field);
				updateRedux(field);
				handleAction("move", field.id, checked);
			}
		});
	};

	const handleChangeData = (value, name) => {
		fields.forEach((field) => {
			if (field.id === name) {
				if (value === null) {
					field.value = "";
					field.valueAsString = "";
				} else {
					field.value = moment.utc(value, "YYYY-MM-DD").valueOf();
				}

				field.editingDateTime = true;
				clearValidation(field);
				updateRedux(field);
				handleAction("move", field.id, field.value);
			}
		});
	};
		
	const handleChangeDateTime = (value, name) => {
		fields.forEach((field) => {
			if (field.id === name) {
				let ipochDate = "";
				ipochDate = value ? moment(value).utc().format("x") : moment().utc().format("x");
				field.value = parseInt(ipochDate);
				field.editingDateTime = true;


				if (value === undefined) {
					field.value = "";
					field.valueAsString = "";
				}

				clearValidation(field);
				updateRedux(field);
				handleAction("move", field.id, field.value);
			}
		});
	};

	const handleChangeTime = (value, name) => {
		fields.forEach((field) => {
			if (field.id === name) {
				let ipochDate = "";
				ipochDate = moment(value, "HH:mm")
					.tz(userTimezone || defaultUserTimezone)
					.format("x");
				field.value = parseInt(ipochDate);

				clearValidation(field);
				updateRedux(field);
				handleAction("move", field.id, field.value);
			}
		});
	};

	const handleBlurDate = (value, name) => {
		fields.forEach((el) => {
			if (el.id === name) {
				let ipochDate = "";
				ipochDate = moment.utc(value, 'YYYY-MM-DD').unix() * 1000;
				el.value = parseInt(ipochDate);
				el.valueAsString = moment(value).format("DD/MM/YYYY");
				el["editingDateTime"] = false;
				updateRedux(el);
				handleAction("move", el.id, parseInt(ipochDate));
			}
		});
	};

	const handleBlurDateTime = (value, name) => {
		fields.forEach((el) => {
			if (el.id === name) {
				let ipochDate = "";
				ipochDate = moment(value).utc().format("x");
				el.value = parseInt(ipochDate);
				el.valueAsString = moment(value).format("DD/MM/YYYY");
				el["editingDateTime"] = false;
				updateRedux(el);
				handleAction("move", el.id, parseInt(ipochDate));
			}
		});
	};

	const handleBlurTime = (value, name) => {
		fields.forEach((el) => {
			if (el.id === name) {
				let ipochDate = "";
				ipochDate = moment(value, "HH:mm")
					.tz(userTimezone || defaultUserTimezone)
					.format("x");
				el.value = parseInt(ipochDate);
				updateRedux(el);
				handleAction("move", el.id, parseInt(ipochDate));
			}
		});
	};


	const handleClick = (e, type, id, title = null, scopeId, fileScope) => {
		e && e.persist && e.persist();
		switch (type) {
			case "button":
				handleAction("click", id, title);
				break;
			case "uploadFile": {
				const file = e.target.files;
				if (!file) {
					return;
				};

				const fileDetailObj = file[0];
				const fileName = file && file[0] ? file[0].name : "";
				let createFileName = getFileName();

				createFileName += fileName.substr(
					fileName.lastIndexOf("."),
					fileName.length - 1
				);

				setIsFileUploading((old) => {
					return [...old, id];
				});

				uploadingTheFile(
					fileDetailObj,
					conversation,
					createFileName,
					fileScope,
					scopeId
				)
					.then((data) => {
						fields.forEach((el) => {
							if (el.id === e.target.name) {
								el.value = data.split("/").pop();
								el.fileName = fileName;
								updateRedux(el);

								const currentFieldValue = {
									currentFieldValue: el.value,
									fileName: el.fileName,
								};

								handleAction("move", el.id, currentFieldValue, true);
								setDisableSubmit(true);
							}
						});
						setIsFileUploading((old) => {
							return old.filter((item) => item !== id);
						});
					})
					.catch((err) => {
						setIsFileUploading((old) => {
							return old.filter((item) => item !== id);
						});
					});
				break;
			}
			case "removeUploadFile": {
				let custEvent = {
					target: {
						name: id,
					},
				};
				fields.forEach((el) => {
					if (el.id === custEvent.target.name) {
						handleAction(
							"move",
							el.id,
							{
								currentFieldValue: "",
								fileName: "",
							},
							true
						);
					}
				});
				handleChange(custEvent, type);
				setDisableSubmit(false);
				setIsFileUploading((old) => {
					return old.filter((itm) => itm !== id);
				});
				break;
			}
			case "image_field": {
				// e is reference to underlying input for image_field, not event
				let file = e.files;
				if (!file) {
					return;
				}
				let fileDetailObj = file[0];
				let fileName = file && file[0] ? file[0].name : "";
				let createFileName = getFileName();
				createFileName += fileName.substr(
					fileName.lastIndexOf("."),
					fileName.length - 1
				);
				setIsFileUploading((old) => {
					return [...old, id];
				});

				uploadingTheFile(
					fileDetailObj,
					scopeId || conversation,
					createFileName,
					fileScope || "conversations"
				)
					.then((data) => {
						fields.forEach((el) => {
							if (el.id === e.name) {
								el.value = data.split("/").pop();
								el.fileName = fileName;
								updateRedux(el);
								handleAction(
									"move",
									el.id,
									{
										currentFieldValue: el.value,
										fileName: el.fileName,
									},
									true
								);
							}
						});
						setIsFileUploading((old) => {
							return old.filter((item) => item !== id);
						});
					})
					.catch((err) => {
						console.log("on error === ", err);
						setIsFileUploading((old) => {
							return old.filter((item) => item !== id);
						});
					});
				break;
			}
			default:
				break;
		}
	};

	const handleMapAreasClick = (
		field,
		type,
		id,
		title = null,
		map_new_areas_array
	) => {
		let areas_value = (Array.isArray(field?.value) && field?.value) || [];
		areas_value = areas_value.filter((area_val) =>
			Array.isArray(area_val.value)
		);
		if (map_new_areas_array.length === 0) {
			handleAction("move", id, areas_value);
			return;
		}
		if (areas_value.length === 0) {
			handleAction("move", id, map_new_areas_array);
			return;
		}
		handleAction("move", id, [...areas_value, ...map_new_areas_array]);
	};

	const onSelectField = (id, value = "", action = "move") => {
		handleAction(action, id, value);
	};


	const updatePromptOnCloseModal = (conversation, promptOnClose) => {
		dispatch(handlePromptOnClose(conversation, promptOnClose));
	};


	const disableFormSubmit = (value) => {
		setDisableSubmit(value);
	};

	// Utility: Clears validation flags from a field
	const clearValidation = (field) => {
		delete field.validationResult;
		delete field.validationMessage;
	};

	const renderFields = (field, isSections, section) => {
		let component = null;
		if (!field.hidden) {
			switch (field.type) {
				case "text_field":
				case "email_field":
					if (section?.readOnly) {
						component = <TextField {...field} />
						break;
					}
				case "number":
				case "text_area":
				case "password_field":
					component = (
						<StyledTableCell align="left" className="hihtligted-text">
							<TextInput
								{...field}
								readOnly={options?.readOnly ? true : field.readOnly}
								isSmallScreen={!largeScreen}
								isSections={isSections}
								userDomain={userDomain}
								handleChange={handleChange}
								isModal={true}
								onBlurField={onBlurField}
								className="!font-bold"
							/>
						</StyledTableCell>
					);
					break;
				case "rich_text_area":
					component = (
						<StyledTableCell align="left" >
							<RichTextInput
								field={field}
								editorState={editorState}
								onEditorStateChange={onEditorContentChange}
								onBlurField={onBlurRichEditorField}
							/>
						</StyledTableCell>
					);
					break;
				case "file_field":
					component = (
						<StyledTableCell colSpan={2}>
							<FileUploadField
								{...field}
								readOnly={options?.readOnly ? true : field.readOnly}
								isFileUploading={isFileUploading}
								isSmallScreen={!largeScreen}
								isSections={isSections}
								handleClick={handleClick}
								conversationId={conversation}
								addManuallyRow={addManuallyRow}
							/>
						</StyledTableCell>
					);
					break;
				case "radiobutton":
					component = (
						<StyledTableCell align="left">
							<RadioInput
								{...field}
								readOnly={options?.readOnly ? true : field.readOnly}
								isSmallScreen={!largeScreen}
								isSections={isSections}
								handleChange={handleChange}
								onBlurField={onBlurField}
							/>
						</StyledTableCell>
					);
					break;
				case "dropdown":
					component = (
						<StyledTableCell align="left">
							<SelectInput
								{...field}
								readOnly={options?.readOnly ? true : field.readOnly}
								isSmallScreen={!largeScreen}
								isSections={isSections}
								multiple={false}
								handleChange={handleChange}
								onBlurField={onBlurField}
							/>
						</StyledTableCell>
					);
					break;
				case "object_multi_selection":
					component = (
						<StyledTableCell align="left">
							<ObjectInput2
								{...field}
								readOnly={options?.readOnly ? true : field.readOnly}
								isSmallScreen={!largeScreen}
								isSections={isSections}
								multiple={true}
								handleChange={handleChange}
								onBlurField={onBlurField}
							/>
						</StyledTableCell>
					);
					break;
				case "multi_selection":
					component = (
						<StyledTableCell align="left">
							<SelectInput
								{...field}
								readOnly={options?.readOnly ? true : field.readOnly}
								isSmallScreen={!largeScreen}
								isSections={isSections}
								multiple={true}
								handleChange={handleChange}
								onBlurField={onBlurField}
							/>
						</StyledTableCell>
					);
					break;
				case "buttons_field":
					component = (
						<StyledTableCell>
							<ButtonsField
								{...field}
								readOnly={options?.readOnly ? true : field.readOnly}
								isSmallScreen={!largeScreen}
								handleClick={handleClick}
							/>
						</StyledTableCell>
					);
					break;
				case "switch":
					component = (
						<StyledTableCell align="left">
							<SwitchInput
								{...field}
								readOnly={options?.readOnly ? true : field.readOnly}
								isSmallScreen={!largeScreen}
								isSections={isSections}
								handleChangeSwitch={handleChangeSwitch}
							/>
						</StyledTableCell>
					);
					break;
				case "checkbox":
				case "boolean_field":
					component = (
						<StyledTableCell align="left">
							<CheckboxInput
								{...field}
								readOnly={options?.readOnly ? true : field.readOnly}
								isSmallScreen={!largeScreen}
								isSections={isSections}
								handleChangeCheck={handleChangeCheck}
								handleChangeBooleanCheck={handleChangeBooleanCheck}
							/>
						</StyledTableCell>
					);
					break;
				case "date":
				case "time":
				case "datetime":
					component = (
						<StyledTableCell align="left">
							<DateTimeInput
								{...field}
								readOnly={options?.readOnly ? true : field.readOnly}
								isSmallScreen={!largeScreen}
								isSections={isSections}
								disableFormSubmit={disableFormSubmit}
								handleChangeDateTime={handleChangeDateTime}
								handleBlurDateTime={handleBlurDateTime}
								handleChangeTime={handleChangeTime}
								handleBlurTime={handleBlurTime}
								handleBlurDate={handleBlurDate}
								handleChangeData={handleChangeData}

							/>
						</StyledTableCell>
					);
					break;
				case "slider": // TODO
					break;
				case "image_field":
					component = (
						<StyledTableCell align="left">
							<ImageUploadField
								{...field}
								readOnly={options?.readOnly ? true : field.readOnly}
								isFileUploading={isFileUploading}
								isSmallScreen={!largeScreen}
								isSections={isSections}
								handleClick={handleClick}
							/>
						</StyledTableCell>
					);
					break;
				case "lookup":
					component = (
						<StyledTableCell align="left">
							<LookupInput
								{...field}
								isSearchedFrontedKey={options?.isSearchedFrontedKey ?? false}
								readOnly={options?.readOnly ? true : field.readOnly}
								isSmallScreen={!largeScreen}
								isSections={isSections}
								handleChange={handleChange}
								onBlurField={onBlurField}
								onSelectField={onSelectField}
								activeLookupId={activeLookupId}
								setActiveLookupId={setActiveLookupId}
							/>
						</StyledTableCell>
					);
					break;
				case "map_area":
					const mapContainer = `container-for-rendering-map-ina-field-${field?.id}`;

					component = (
						<StyledTableCell align="left">
							<FMMap
								field={field}
								conversation={conversation}
								options={field.options}
								message={field.rows}
								showAreaList={false}
								noSideDrawer={true}
								noFullScreencontrol={false}
								nomagnifyerCntrl={false}
								nosettingsCntrl={false}
								limitedSettingsCntrl={true}
								nomapsearchCntrl={true}
								handleMapAreasClick={handleMapAreasClick}
								mapContainer={mapContainer}
								mapCssStyles={{
									height: `400px`,
									width: `100%`,
									borderRadius: "20px",
								}}
							/>
						</StyledTableCell>
					);
					break;
				case "phone_number":
					component = (
						<StyledTableCell align="left">
							<BasicPhoneNumberField
								{...field}
								readOnly={options?.readOnly ? true : field.readOnly}
								isSmallScreen={!largeScreen}
								isSections={isSections}
								handleChange={handleChange}
								onBlurField={onBlurField}
							/>
						</StyledTableCell>
					);
					break;
				case "color_picker":
					component = (
						<StyledTableCell align="left">
							<ColorPickerField
								{...field}
								readOnly={options?.readOnly ? true : field.readOnly}
								isSmallScreen={!largeScreen}
								isSections={isSections}
								handleChange={handleChange}
								onBlurField={onBlurField}
							/>
						</StyledTableCell>
					);
					break;
				case "blank":
					component = (
						<StyledTableCell align="left">
							<Box
								style={{
									minHeight: `5px`,
									width: `10vw`,
								}}
							/>
						</StyledTableCell>
					);
					break;
				default:
					component = null;
			}
		}
		return component;
	};

	const hasSections = options.sections && Object.keys(options.sections).length > 0;
	const showTitle = !parentMessageType;

	const TitleBar = showTitle && (
		<CustomizedGrid
			container
			direction="row"
			justifyContent="space-between"
			alignItems="center"
		>
			<div className="text-size-14 font-semibold text-[#2C2F44] ml-3 ">{options?.title || ""}</div>
		</CustomizedGrid>
	);

	return (
		<>
			{TitleBar}
			{hasSections ? (
				<FMSectionsFormComponent
					conversation={conversation}
					fields={fields}
					options={options}
					parentTabId={parentTabId}
					parentDocId={parentDocId}
					handleAction={handleAction}
					updateRedux={updateRedux}
					handleConfirm={handleConfirm}
					handleCancel={handleCancel}
					genericError={genericError}
					renderFields={renderFields}
					onCardAction={onCardAction}
					disableSubmit={disableSubmit}
					setDisableSubmit={setDisableSubmit}
					collectionData={collectionData}
					dashboardsData={dashboardsData}
					cardsSetData={cardsSetData}
					handleCheckSubmitButton={handleCheckSubmitButton}
				/>
			) : (
				<div className="w-[40%]">
					<FMRegularFormComponent
						conversation={conversation}
						fields={fields}
						options={options}
						handleAction={handleAction}
						updateRedux={updateRedux}
						handleConfirm={handleConfirm}
						handleCancel={handleCancel}
						genericError={genericError}
						renderFields={renderFields}
						disableSubmit={disableSubmit}
						setDisableSubmit={setDisableSubmit}
					/>
				</div>
			)}
		</>
	)
}

export default FMFormComponent;
